<template>
  <div>
    <div class="fix-wrapper">
      <breadcrumb
        :crumbs="[$t('menu.purchases'), $t('text.create')]"
      ></breadcrumb>
      <div class="container">
        <el-form
          ref="purchaseForm"
          :model="purchase"
          label-width="100px"
          size="small"
        >
          <div class="pd20">
            <fieldset>
              <legend>
                {{ $t("text.baseInfo") }}
                <el-checkbox v-model="purchase.hurry" class="float-right">{{
                  $t("order.hurry")
                }}</el-checkbox>
              </legend>

              <el-row :gutter="20">
                <el-col :span="8">
                  <el-form-item
                    :label="$t('purchase.serialNo')"
                    :prop="'serialNo'"
                    :rules="[
                      {
                        required: true,
                        validator: validateSerialNo,
                        trigger: 'blur',
                      },
                    ]"
                  >
                    <el-input
                      size="small"
                      v-model.trim="purchase.serialNo"
                      :placeholder="$t('prompt.alphabetNumberLine')"
                      clearable
                    >
                      <template slot="append">
                        <svg-icon
                          icon-class="serialNo"
                          style="font-size: 24px"
                          @click="clickSetSerialNo"
                        ></svg-icon>
                      </template>
                    </el-input>
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item
                    :label="$t('menu.suppliers')"
                    :prop="'supplierId'"
                    :rules="[
                      {
                        required: true,
                        message: $t('rules.required', [$t('menu.suppliers')]),
                        trigger: 'blur',
                      },
                    ]"
                  >
                    <el-select
                      size="small"
                      v-model="purchase.supplierId"
                      filterable
                      clearable
                      @change="selectSupplier"
                    >
                      <el-option
                        v-for="supplier in suppliers"
                        :key="supplier.id"
                        :label="supplier.name"
                        :value="supplier.id"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
                <el-col :span="8">
                  <el-form-item
                    :label="$t('text.handOverAt')"
                    :prop="'handOverAt'"
                    :rules="[
                      {
                        required: true,
                        message: $t('rules.required', [$t('text.handOverAt')]),
                        trigger: 'blur',
                      },
                    ]"
                  >
                    <el-date-picker
                      size="small"
                      v-model="purchase.handOverAt"
                      type="date"
                      style="width: 100%"
                      :picker-options="pickerTodayAfterTime"
                    />
                  </el-form-item>
                </el-col>
              </el-row>
            </fieldset>
            <fieldset>
              <legend>
                {{ $t("order.productInfo") }}
              </legend>
              <el-row :gutter="20">
                <el-col :span="16">
                  <el-row :gutter="10">
                    <el-col :span="12">
                      <el-row>
                        <el-col :span="23">
                          <el-form-item
                            :label="$t('product.productNo')"
                            :prop="'productNo'"
                            :rules="[
                              {
                                required: true,
                                message: $t('rules.required', [
                                  $t('product.productNo'),
                                ]),
                                trigger: 'blur',
                              },
                            ]"
                          >
                            <el-autocomplete
                              v-model.trim="purchase.productNo"
                              :fetch-suggestions="querySearchProductNo"
                              :trigger-on-focus="false"
                              style="width: 100%"
                              @blur="blurProductNo(purchase)"
                              @select="handleSelectProductNo"
                            >
                              <template slot-scope="{ item }">
                                <div class="name">{{ item.productNo }}</div>
                                <span class="addr">{{
                                  item.productNoAlias
                                }}</span>
                              </template>
                            </el-autocomplete>
                          </el-form-item>
                        </el-col>
                        <el-col :span="1">
                          <el-form-item label-width="2px">
                            <el-popover
                              placement="top-start"
                              width="100"
                              trigger="hover"
                            >
                              <div class="grey-shallow">点击查看采购历史</div>
                              <i
                                slot="reference"
                                class="el-icon-tickets"
                                @click="purchaseHistory"
                              ></i>
                            </el-popover>
                          </el-form-item>
                        </el-col>
                      </el-row>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item
                        :label="$t('product.category')"
                        :prop="'categoryId'"
                      >
                        <el-select
                          size="small"
                          v-model="purchase.categoryId"
                          clearable
                          @change="selsectCategory"
                        >
                          <el-option
                            v-for="category in categories"
                            :key="category.id"
                            :label="category.name"
                            :value="category.id"
                          ></el-option>
                        </el-select>
                      </el-form-item>
                    </el-col>
                  </el-row>
                  <el-row :gutter="10">
                    <el-col :span="12">
                      <el-form-item :label="$t('product.size')" :prop="'size'">
                        <el-autocomplete
                          size="small"
                          v-model.trim="purchase.size"
                          :fetch-suggestions="querySearchSize"
                          style="width: 100%"
                          @select="handleSelectSize"
                        />
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item
                        :label="$t('product.color')"
                        :prop="'color'"
                      >
                        <el-autocomplete
                          size="small"
                          v-model.trim="purchase.color"
                          :fetch-suggestions="querySearchColor"
                          style="width: 100%"
                          @select="handleSelectColor"
                        />
                      </el-form-item>
                    </el-col>
                  </el-row>
                  <el-row :gutter="20">
                    <el-col :span="12">
                      <el-form-item
                        :label="$t('purchase.price')"
                        :prop="'price'"
                        :rules="[
                          {
                            required: true,
                            message: $t('rules.required', [
                              $t('purchase.price'),
                            ]),
                            trigger: 'blur',
                          },
                        ]"
                      >
                        <el-input size="small" v-model.trim="purchase.price">
                          <!-- <div slot="append">
                            {{ purchase._productPrice | currency }}
                          </div> -->
                        </el-input>
                      </el-form-item>
                    </el-col>
                    <el-col :span="12">
                      <el-form-item
                        :label="$t('text.quantity')"
                        :prop="'quantity'"
                        :rules="[
                          {
                            required: true,
                            message: $t('rules.required', [
                              $t('text.quantity'),
                            ]),
                            trigger: 'blur',
                          },
                        ]"
                      >
                        <el-row>
                          <el-col :span="14">
                            <el-input
                              size="small"
                              v-model.trim="purchase.quantity"
                            ></el-input>
                          </el-col>
                          <el-col :span="10">
                            <el-select
                              size="small"
                              v-model="purchase.unit"
                              clearable
                            >
                              <el-option
                                v-for="unit in units"
                                :key="unit"
                                :label="unit"
                                :value="unit"
                              ></el-option>
                            </el-select>
                          </el-col>
                        </el-row>
                      </el-form-item>
                    </el-col>
                  </el-row>
                </el-col>
                <el-col :span="8">
                  <el-form-item
                    :label="$t('order.colorImage')"
                    :prop="'colorImage'"
                  >
                    <upload-single-picture v-model="purchase.colorImage" />
                  </el-form-item>
                </el-col>
              </el-row>
            </fieldset>
            <fieldset class="mgb15">
              <legend>
                {{ $t("product.specJson") }}
                <el-button
                  v-if="productNoAttributes.length != 0"
                  type="warning"
                  size="mini"
                  @click="clearSpecJson"
                >
                  {{ $t("action.clearSpecJson") }}
                </el-button>
              </legend>
              <el-row :gutter="20" class="mgb10">
                <el-col
                  v-for="attribute in productNoAttributes"
                  :key="attribute.name"
                  :span="6"
                  class="mgb10"
                >
                  <el-input v-model="attribute.value" size="small">
                    <template slot="prepend">
                      <div>
                        <raw-html-limit-number
                          :content="attribute.name"
                          :number="6"
                        />
                      </div>
                    </template>
                  </el-input>
                </el-col>
              </el-row>
            </fieldset>
            <el-row :gutter="20">
              <el-col :span="16">
                <fieldset class="mgb15">
                  <legend>
                    {{ $t("product.images") }}
                    <svg-icon icon-class="link" @click="linkImage" />
                  </legend>
                  <dropzone v-model="purchase.images" multiple />
                </fieldset>
              </el-col>
              <el-col :span="8">
                <fieldset class="mgb15">
                  <legend>{{ $t("product.attachment") }}</legend>
                  <div class="mgb10">
                    <small class="orange">{{
                      $t("product.attachmentExplain")
                    }}</small>
                  </div>
                  <attachment v-model="purchase.attachment" />
                </fieldset>
              </el-col>
            </el-row>
            <fieldset class="mgb15">
              <legend>{{ $t("text.remark") }}</legend>
              <tinymce-editor
                ref="editor"
                v-model="purchase.remark"
                :height="300"
              ></tinymce-editor>
            </fieldset>
          </div>
          <div class="form-footer">
            <el-button
              type="primary"
              size="medium"
              plain
              :loading="saveDoing"
              @click="saveSubmit('submit')"
              >{{ $t("text.submit") }}</el-button
            >
            <el-button
              type="success"
              size="medium"
              plain
              :loading="saveDoing"
              @click="saveSubmit('submitAndPlace')"
              >{{ $t("text.submit") }}并{{ $t("action.place") }}</el-button
            >
            <back-button />
          </div>
        </el-form>
      </div>
    </div>

    <el-dialog
      :title="$t('action.linkImage')"
      :visible.sync="linkImageDF"
      :close-on-click-modal="false"
      width="50%"
    >
      <el-form ref="linkImageForm" :model="linkImageObj">
        <el-form-item
          :prop="'linkImageStr'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('action.linkImage')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            v-model="linkImageObj.linkImageStr"
            :placeholder="$t('prompt.linkImageExplain')"
            type="textarea"
            :autosize="{ minRows: 4, maxRows: 10 }"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="linkImageDoing"
          size="small"
          @click="linkImageSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="linkImageDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>

    <el-dialog
      :title="$t('action.viewPurchaseHistory')"
      :visible.sync="purchaseHistoryFD"
      :close-on-click-modal="false"
      width="50%"
    >
      <el-form>
        <vxe-table
          :data="purchaseHistories"
          id="purchaseHistoriesTable"
          row-id="id"
          ref="purchaseHistoriesTable"
          class="recordsTable"
          size="mini"
          resizable
          round
          auto-resize
          highlight-hover-row
          :scroll-y="{ enabled: false }"
        >
          <vxe-table-column field="price" :title="$t('purchase.price')">
            <template v-slot="{ row }">
              {{ row.price | currency }}
            </template>
          </vxe-table-column>
          <vxe-table-column
            field="supplierName"
            :title="$t('menu.suppliers')"
          />
          <vxe-table-column field="color" :title="$t('product.color')" />
          <vxe-table-column field="size" :title="$t('product.size')" />
          <vxe-table-column field="createAt" :title="$t('text.createAt')" />
        </vxe-table>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="purchaseHistoryFD = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import axios from "axios";
import Vue from "vue";
import ls from "store2";
import Util from "@/utils/index.js";
import { page } from "@/utils/mixins.js";

import UploadSinglePicture from "@/components/UploadSinglePicture";
import Dropzone from "@/components/Dropzone";
import Attachment from "@/components/Attachment";
import TinymceEditor from "@/components/TinymceEditor";
import BackButton from "@/components/BackButton";

//
import Purchase from "@/api/purchase.js";
import Supplier from "@/api/supplier.js";

import Category from "@/api/category.js";
import Size from "@/api/size.js";
import Enumeration from "@/api/enumeration.js";
import Color from "@/api/color.js";
import Product from "@/api/product.js";

export default {
  mixins: [page],
  components: {
    UploadSinglePicture,
    Dropzone,
    Attachment,
    TinymceEditor,
    BackButton,
  },
  data() {
    var validateSerialNo = (rule, value, callback) => {
      const self = this;
      if (value === null || value === "") {
        callback(
          new Error(this.$t("rules.required", [this.$t("purchase.serialNo")]))
        );
        return;
      }
      if (!Util.regexAlphabetNumberLine(value)) {
        callback(new Error(this.$t("prompt.regexAlphabetNumberLine")));
        return;
      }
      Purchase.findBySerialNo({ serialNo: value }).then(function (res) {
        if (res.data.code === 200) {
          callback(
            new Error(self.$t("rules.exist", [self.$t("purchase.serialNo")]))
          );
        } else {
          callback();
        }
      });
    };

    return {
      validateSerialNo: validateSerialNo,
      copyPurchase: null,
      suppliers: [],
      categories: [],
      sizes: [],
      units: [],
      colors: [],
      attributes: [],
      pickerTodayAfterTime: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7;
        },
      },
      purchase: {
        serialNo: null,
        supplierId: null,
        supplierName: null,
        supplierTenantId: null,
        customerName: null,
        customerPhone: null,
        customerCountry: null,
        customerProvince: null,
        customerCity: null,
        customerAddress: null,
        hurry: false,
        handOverAt: null,

        productNo: null,
        categoryId: null,
        categoryName: null,
        color: null,
        colorImage: null,
        size: null,
        quantity: null,
        unit: null,
        price: null,
        images: null,
        remark: null,
        specJson: null,
        enclosure: null,
        craftwork: null,
        attachment: null,
        currency: "CNY",
        replaceDeliver: false,
        workflowState: null,
        type: null,
        _productIntroduction: null,
        // _productPrice: null,
        _oldSizeExplain: null,
        _productId: null,
      },
      saveDoing: false,

      productNoColors: [],
      productNoSizes: [],
      productNoAttributes: [],
      products: [],
      categorySizes: [],

      productSuppliers: [],

      // 图片链接
      linkImageDF: false,
      linkImageObj: {
        linkImageStr: null,
      },
      linkImageDoing: false,
      // 采购历史
      purchaseHistories: [],
      purchaseHistoryFD: false,
    };
  },
  mounted() {},
  created() {
    const self = this;
    axios
      .all([
        Purchase.findById(self.$route.params.id),
        Supplier.findByTenantId(),
        Category.findByTenantId(),
        Size.findByTenantId(),
        Enumeration.findByType("unit"),
        Color.findByTenantId(),
        Enumeration.findByType("attribute"),
      ])
      .then(
        axios.spread(function (
          purchase,
          suppliers,
          categories,
          sizes,
          units,
          colors,
          attributes
        ) {
          if (purchase.data.code === 200) {
            self.copyPurchase = purchase.data.entity;
          } else {
            self.sysError();
          }
          if (suppliers.status === 200) {
            self.suppliers = suppliers.data;
          } else {
            self.sysError();
          }
          if (categories.status === 200) {
            self.categories = categories.data;
          } else {
            self.sysError();
          }
          if (sizes.status === 200) {
            self.sizes = sizes.data;
          } else {
            self.sysError();
          }
          if (units.data.code === 200) {
            self.units = JSON.parse(units.data.entity.itemsJson);
            self.purchase.unit = self.units.length != 0 ? self.units[0] : null;
          } else {
            self.sysError();
          }
          if (colors.status === 200) {
            self.colors = colors.data;
          } else {
            self.sysError();
          }
          if (attributes.data.code === 200) {
            self.attributes = JSON.parse(attributes.data.entity.itemsJson);
          } else {
            self.sysError();
          }
          self.initData();
        })
      );
  },

  methods: {
    clearSpecJson() {
      if (this.productNoAttributes.length != 0) {
        for (const productNoAttribute of this.productNoAttributes) {
          productNoAttribute.value = null;
        }
      }
    },
    //
    initData() {
      const self = this;
      if (
        self.suppliers.find((s) => s.id == self.copyPurchase.supplierId) != null
      ) {
        self.purchase.supplierId = self.copyPurchase.supplierId;
        self.purchase.supplierName = self.copyPurchase.supplierName;
        self.purchase.supplierTenantId = self.copyPurchase.supplierTenantId;
      }
      self.purchase.hurry = self.copyPurchase.hurry;
      self.purchase.handOverAt = self.copyPurchase.handOverAt;
      self.purchase.productNo = self.copyPurchase.productNo;
      if (
        self.categories.find((c) => c.id == self.copyPurchase.categoryId) !=
        null
      ) {
        self.purchase.categoryId = self.copyPurchase.categoryId;
        self.purchase.categoryName = self.copyPurchase.categoryName;
        self.selsectCategory();
      }
      self.purchase.color = self.copyPurchase.color;
      self.purchase.colorImage = self.copyPurchase.colorImage;
      self.purchase.size = self.copyPurchase.size;
      self.purchase.quantity = self.copyPurchase.quantity;
      self.purchase.unit = self.copyPurchase.unit;
      self.purchase.size = self.copyPurchase.size;
      self.purchase.price = self.copyPurchase.price;
      self.purchase.images = self.copyPurchase.images;
      self.purchase.remark = self.copyPurchase.remark;
      self.purchase.enclosure = self.copyPurchase.enclosure;
      self.purchase.craftwork = self.copyPurchase.craftwork;
      self.purchase.attachment = self.copyPurchase.attachment;
      self.purchase.specJson = self.copyPurchase.specJson;
      self.initDataProductNo();
    },
    initDataProductNo() {
      const self = this;
      if (self.purchase.productNo != null && self.purchase.productNo != "") {
        Product.findByProductNo({ productNo: self.purchase.productNo }).then(
          function (res) {
            if (res.data.code === 200) {
              const item = res.data.entity;
              self.purchase._productId = item.id;
              self.purchase._productIntroduction = item.introduction;
              self.productNoColors = item.colors;
              self.productNoSizes = item.sizes;
              if (
                item.customAttributeJson != null &&
                item.customAttributeJson !== "[]"
              ) {
                self.productNoAttributes = JSON.parse(
                  item.customAttributeJson
                ).map((att) => {
                  return { value: null, name: att };
                });
              } else {
                self.productNoAttributes = self.attributes.map((att) => {
                  return { value: null, name: att };
                });
              }
              //
              const specJsonArrs = JSON.parse(self.purchase.specJson || "[]");
              if (self.productNoAttributes.length != null) {
                for (const productNoAttribute of self.productNoAttributes) {
                  if (
                    specJsonArrs.find(
                      (sp) => sp.name === productNoAttribute.name
                    ) != null
                  ) {
                    productNoAttribute.value = specJsonArrs.find(
                      (sp) => sp.name === productNoAttribute.name
                    ).value;
                  }
                }
              }
              //
              if (self.purchase._productId != null) {
                Product.productSuppliersfindById(self.purchase._productId).then(
                  function (res) {
                    if (res.status === 200) {
                      self.productSuppliers = res.data;
                    } else {
                      self.sysError();
                    }
                  }
                );
              }
            } else {
              self.purchase._productIntroduction = "";
              const specJsonArr = JSON.parse(
                self.copyPurchase.specJson || "[]"
              );
              if (self.attributes.length !== 0) {
                for (const att of self.attributes) {
                  self.productNoAttributes.push({
                    value:
                      specJsonArr.find((s) => s.name === att) != null
                        ? specJsonArr.find((s) => s.name === att).value
                        : null,
                    name: att,
                  });
                }
              }
            }
          }
        );
      }
    },

    clickSetSerialNo() {
      this.purchase.serialNo = Util.serialNo();
    },
    // 图片链接
    linkImage() {
      this.linkImageDF = true;
      this.linkImageObj.linkImageStr = null;
    },
    linkImageSubmit() {
      const self = this;
      this.$refs.linkImageForm.validate((valid) => {
        if (valid) {
          const linkImageArr = this.linkImageObj.linkImageStr.split("\n");
          const linkImageArrFilter = linkImageArr.filter((a) => a != null);
          if (self.purchase.images == null) {
            self.purchase.images = linkImageArrFilter.join(";");
          } else {
            self.purchase.images =
              self.purchase.images + ";" + linkImageArrFilter.join(";");
          }
          this.linkImageDF = false;
        } else {
          return false;
        }
      });
    },
    //
    purchaseHistory() {
      const self = this;
      if (self.purchase.productNo == null || self.purchase.productNo == "") {
        self.$message({
          message: self.$t("rules.required", [self.$t("product.productNo")]),
          type: "warning",
        });
        return;
      }
      self.purchaseHistories = [];
      const obj = {
        productNo: self.purchase.productNo,
        size: 10,
      };
      Purchase.history(obj).then(function (res) {
        if (res.status === 200) {
          self.purchaseHistories = res.data;
          self.purchaseHistoryFD = true;
        } else {
          self.sysError();
        }
      });
    },
    //
    selectSupplier() {
      const self = this;
      self.getProductSuppliers();
    },
    getProductSuppliers() {
      const self = this;
      if (self.purchase._productId != null) {
        Product.productSuppliersfindById(self.purchase._productId).then(
          function (res) {
            if (res.status === 200) {
              self.productSuppliers = res.data;
              self.computePrice();
            } else {
              self.sysError();
            }
          }
        );
      }
    },
    // 货号
    querySearchProductNo(queryString, cb) {
      const self = this;
      //
      self.clearProduct();
      //
      if (queryString == null || queryString.length < 1) {
        return;
      }
      Product.vagueFindByProductNo({ productNo: queryString }).then(function (
        res
      ) {
        self.products = res.data;
        if (self.products.length !== 0) {
          for (const p of self.products) {
            Vue.set(p, "value", p.productNo);
          }
        }
        cb(self.products);
      });
    },
    handleSelectProductNo(item) {
      const self = this;
      self.purchase.productNo = item.productNo;
      self.blurProductNo(item);
    },
    blurProductNo(item) {
      const self = this;
      if (item.productNo != null) {
        Product.findByProductNo({
          productNo: item.productNo,
        }).then(function (res) {
          if (res.data.code === 200) {
            const item = res.data.entity;
            if (self.copyPurchase.productNo != item.productNo) {
              self.purchase.categoryId = item.categoryId;
              self.purchase.categoryName = item.categoryName;
              self.purchase.color = null;
              self.purchase.colorImage = null;
              self.purchase.size = null;
              self.purchase.unit = item.unit;
              self.purchase.images = item.images;
              self.purchase.craftwork = item.craftwork;
              self.purchase.remark =
                (self.purchase.remark || "") + (item.introduction || "");
            }
            self.selsectCategory();
            self.purchase._productIntroduction = item.introduction;
            self.productNoColors = item.colors;
            self.productNoSizes = item.sizes;
            if (
              item.customAttributeJson != null &&
              item.customAttributeJson !== "[]"
            ) {
              self.productNoAttributes = JSON.parse(
                item.customAttributeJson
              ).map((att) => {
                return { value: null, name: att };
              });
            }
            self.purchase._productId = item.id;
            self.getProductSuppliers();
          } else {
            self.clearProduct();
          }
        });
      }
    },
    clearProduct() {
      const self = this;
      self.purchase.categoryId = null;
      self.purchase.categoryName = null;
      self.selsectCategory();
      self.purchase.color = null;
      self.purchase.colorImage = null;
      self.purchase.size = null;
      self.purchase.unit = self.units.length != null ? self.units[0] : null;
      self.purchase.images = null;
      self.purchase.craftwork = null;
      self.purchase.remark = self.purchase.remark || "";
      // self.purchase.price = null;
      self.purchase._productIntroduction = null;
      self.purchase._oldSizeExplain = null;
      // self.purchase._productPrice = null;
      self.purchase._productId = null;
      self.productSuppliers = [];
      //
      self.productNoColors = self.colors.map((c) => c.name);
      self.productNoSizes = self.sizes.map((s) => s.name);
      self.productNoAttributes = self.attributes.map((att) => {
        return { value: null, name: att };
      });
    },
    selsectCategory() {
      const self = this;
      if (self.purchase.categoryId === "") {
        self.purchase.categoryId = null;
      }
      self.categorySizes = [];
      if (self.purchase.categoryId != null && self.purchase.categoryId !== "") {
        const selsectCategoryObj = self.categories.find(
          (c) => c.id === self.purchase.categoryId
        );
        if (
          selsectCategoryObj &&
          selsectCategoryObj.sizes != null &&
          selsectCategoryObj.sizes.length !== 0
        ) {
          self.categorySizes = selsectCategoryObj.sizes;
        }
      }
    },

    // 颜色
    querySearchColor(queryString, cb) {
      const colors =
        this.productNoColors.length !== 0
          ? this.productNoColors.map((pc) => {
              return { value: pc };
            })
          : this.colors.map((c) => {
              return { value: c.name };
            });
      if (queryString == null) {
        cb(colors);
      } else {
        cb(colors.filter((c) => c.value.indexOf(queryString) !== -1));
      }
    },
    handleSelectColor() {
      this.purchase.color = Util.jyTrim(this.purchase.color);
      //
      this.purchase.colorImage = null;
      const colorObj = this.colors.find((c) => c.name === this.purchase.color);
      if (colorObj != null && colorObj.image != null) {
        this.purchase.colorImage = colorObj.image;
      }
    },
    // 规格
    querySearchSize(queryString, cb) {
      const self = this;
      let sizes = [];
      if (self.productNoSizes.length !== 0) {
        sizes = this.productNoSizes.map((pc) => {
          return { value: pc };
        });
      } else {
        if (self.categorySizes.length !== 0) {
          sizes = this.categorySizes.map((c) => {
            return { value: c.name, explain: c.explain };
          });
        } else {
          sizes = this.sizes.map((c) => {
            return { value: c.name, explain: c.explain };
          });
        }
      }
      if (queryString == null) {
        cb(sizes);
      } else {
        cb(sizes.filter((c) => c.value.indexOf(queryString) !== -1));
      }
    },
    handleSelectSize() {
      const self = this;
      self.purchase.size = Util.jyTrim(self.purchase.size);
      self.computeRemark();
      self.computePrice();
    },
    computeRemark() {
      const self = this;
      const sizeObj = self.sizes.find((s) => s.name === this.purchase.size);
      // 替换已经有的尺码说明
      if (
        self.purchase.remark != null &&
        self.purchase._oldSizeExplain != null
      ) {
        self.purchase.remark = self.purchase.remark.replace(
          ";【" + self.purchase._oldSizeExplain + "】",
          ""
        );
        self.purchase.remark = self.purchase.remark.replace(
          "<br/>【" + self.purchase._oldSizeExplain + "】",
          ""
        );
        self.purchase.remark = self.purchase.remark.replace(
          "【" + self.purchase._oldSizeExplain + "】",
          ""
        );
        self.purchase.remark = self.purchase.remark.replace(
          self.purchase._oldSizeExplain,
          ""
        );
      }
      // 加入新的尺码说明
      if (sizeObj != null && sizeObj.explain != null && sizeObj.explain != "") {
        if (this.purchase.remark !== null && self.purchase.remark != "") {
          self.purchase.remark =
            self.purchase.remark + "<br/>【" + sizeObj.explain + "】";
        } else {
          this.purchase.remark = "【" + sizeObj.explain + "】";
        }
        self.purchase._oldSizeExplain = sizeObj.explain;
      }
    },
    computePrice() {
      const self = this;
      if (
        self.purchase.supplierId != null &&
        self.purchase.supplierId != "" &&
        self.productSuppliers.length != 0 &&
        self.productSuppliers.find(
          (ps) => ps.supplierId == self.purchase.supplierId
        ) != null
      ) {
        const supplierPriceObj = self.productSuppliers.find(
          (ps) => ps.supplierId == self.purchase.supplierId
        );
        if (
          supplierPriceObj.supplierSizePrices.length != 0 &&
          supplierPriceObj.supplierSizePrices.find(
            (sp) => sp.size == self.purchase.size
          ) != null
        ) {
          const supplierSizePriceObj = supplierPriceObj.supplierSizePrices.find(
            (sp) => sp.size == self.purchase.size
          );
          self.purchase.price = supplierSizePriceObj.price;
          return;
        }
        self.purchase.price = supplierPriceObj.price;
        return;
      }

      // self.purchase.price = null;
      return;
    },
    //
    saveSubmit(type) {
      let self = this;
      self.$refs.purchaseForm.validate((valid) => {
        if (valid) {
          if (!Util.regexPrice(self.purchase.price)) {
            self.$message({
              message: self.$t("rules.regex", [self.$t("purchase.price")]),
              type: "warning",
            });
            return;
          }
          if (
            !Util.regexQuantity(self.purchase.quantity) ||
            self.purchase.quantity === "0"
          ) {
            self.$message({
              message: self.$t("rules.regex", [self.$t("text.quantity")]),
              type: "warning",
            });
            return;
          }
          self.purchase.color = Util.jyTrim(self.purchase.color);
          self.purchase.size = Util.jyTrim(self.purchase.size);
          if (self.purchase.categoryId !== null) {
            self.purchase.categoryName = self.purchase.categoryName =
              self.categories.find(
                (s) => s.id === self.purchase.categoryId
              ).name;
          }
          const attributesArr =
            self.productNoAttributes.length !== 0
              ? self.productNoAttributes.filter((sp) => sp.value != null)
              : [];
          self.purchase.specJson = JSON.stringify(attributesArr);
          self.purchase.handOverAt = Util.formatDate(self.purchase.handOverAt);

          self.purchase.supplierName = self.suppliers.find(
            (s) => s.id === self.purchase.supplierId
          ).name;
          self.purchase.supplierTenantId = self.suppliers.find(
            (s) => s.id === self.purchase.supplierId
          ).sourceTenantId;

          self.purchase.customerName = self.Permit.currentUser().name;
          self.purchase.customerPhone = self.Permit.currentUser().phone;
          self.purchase.customerCountry = self.Permit.currentUser().country;
          self.purchase.customerProvince = self.Permit.currentUser().province;
          self.purchase.customerCity = self.Permit.currentUser().city;
          self.purchase.customerAddress = self.Permit.currentUser().address;

          if (type === "submit") {
            self.purchase.workflowState = "newly";
          } else {
            self.purchase.workflowState = "gived";
          }

          self.saveDoing = true;
          Purchase.create(self.purchase).then(function (res) {
            self.saveDoing = false;
            if (res.status === 200) {
              self.$message({
                message: self.$t("prompt.saveSuccess"),
                type: "success",
              });
              self.$router.push({
                name: "pmsPurchases",
              });
              self.$eventHub.$emit("purchaseCreate");
            } else {
              self.sysError();
            }
          });
        } else {
          return false;
        }
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.name {
  text-overflow: ellipsis;
  overflow: hidden;
}
.addr {
  font-size: 12px;
  color: #b4b4b4;
}
</style>